/** A trial length as Paddle describes it on a price (`price.trialPeriod`). */
export interface TrialPeriod {
  interval: 'day' | 'week' | 'month' | 'year';
  frequency: number;
}

/**
 * The free trial the web checkout really includes, or undefined when the Subscribe screen must
 * not promise one (#6145).
 *
 * Read from the Paddle prices this checkout was built with, never assumed: the server decides
 * which prices a caller gets (the ordinary 14-day ones, a referred friend's free-month ones, and
 * after #6552 no-trial ones for somebody who already had a trial), so the prices are the only
 * place the answer is certain. Both plans must carry the same trial, because the heading speaks
 * for the whole screen before the buyer has picked a plan. Any doubt gives undefined, and the
 * screen then keeps its ordinary wording — a promise the checkout does not keep is the one
 * outcome this must never produce.
 *
 * A referred friend's trial of about a month is shown as one month: the referral programme's
 * wording rule is "a free month", never "30 days" (design-docs/referral-program.md). Only when
 * the prices really carry about a month, though — a referral price set up with a shorter trial
 * is shown at its real length rather than promised as a month.
 */
export function offeredTrialPeriod(planTrials: (TrialPeriod | undefined)[], referralFreeMonthApplied: boolean): TrialPeriod | undefined {
  if (planTrials.length === 0) return undefined;
  const first = planTrials[0];
  if (!first || !Number.isInteger(first.frequency) || first.frequency <= 0) return undefined;
  const allSame = planTrials.every(t => t?.interval === first.interval && t.frequency === first.frequency);
  if (!allSame) return undefined;
  if (referralFreeMonthApplied && isAboutOneMonth(first)) return { interval: 'month', frequency: 1 };
  return { interval: first.interval, frequency: first.frequency };
}

// one calendar month, or a day count a month can have (28 to 31)
function isAboutOneMonth(period: TrialPeriod): boolean {
  if (period.interval === 'month') return period.frequency === 1;
  return period.interval === 'day' && period.frequency >= 28 && period.frequency <= 31;
}
